import type { BadgeProps, SxProps, Theme } from '@mui/material';
import { Avatar, Badge, Stack, Typography, useTheme, Tooltip } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';

import Tick from '@/assets/icons/tick.svg?react';

interface CircleIconValueProps {
  indicator?: number | boolean | string | null;
  live?: boolean;
  color?: 'info' | 'secondary' | 'grey';
  label?: string;
  tooltip?: string;
  children: ReactNode;
}

export function CircleIconValue({
  indicator,
  live,
  color = 'info',
  label,
  tooltip,
  children,
}: CircleIconValueProps): ReactElement {
  const theme = useTheme();

  const backgroundColor = {
    info: theme.palette.info.light,
    secondary: theme.palette.secondary.light,
    grey: theme.palette.grey[200],
  }[color];

  const badgeColor: BadgeProps['color'] = typeof indicator === 'boolean' || live ? 'success' : 'primary';
  const isNoIndicator = indicator === undefined || indicator === null;

  let displayBadgeContent: number | ReactElement | string | null;
  if (indicator === null) displayBadgeContent = '?';
  else if (indicator === undefined) displayBadgeContent = null;
  else if (typeof indicator === 'boolean') displayBadgeContent = indicator ? <Tick fontSize="small" /> : null;
  else if (typeof indicator === 'string') displayBadgeContent = indicator.trim() === '' ? '?' : indicator;
  else displayBadgeContent = indicator;

  const badgeSx: SxProps<Theme> = isNoIndicator
    ? { '& .MuiBadge-badge': { backgroundColor: '#ad9999', color: '#ffffff' } }
    : {};

  const content = (
    <Stack alignItems="center" direction="row" gap={1} px={1.5} py={1.25} sx={{ width: 'fit-content' }}>
      <Badge
        showZero
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={displayBadgeContent}
        color={isNoIndicator ? undefined : badgeColor}
        max={999}
        sx={badgeSx}
      >
        <Avatar color="info" sx={{ backgroundColor }}>
          <Stack sx={{ color: 'black' }}>{children}</Stack>
        </Avatar>
      </Badge>
      {label ? <Typography noWrap>{label}</Typography> : null}
    </Stack>
  );

  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
}
